/* eslint-disable */
import { createSlice, AnyAction } from "@reduxjs/toolkit";
import {
  convertISODateTime,
  convertDefaultTimeZoneStringToISO,
} from "@utils/time-helpers";
import type { FileNewViewState } from "@types";
import moment from "moment-timezone";

const initialState: FileNewViewState = {
  location_name: "",
  saveEnabled: true,
  file_id: "",
  title: "",
  description: "",
  scheduled_date: null,
  //For error checking
  originalStartDate: null,
  inputISOScheduledDate: null,
  inputISOLastUpdatedDate: null,
  url: "",
  file_data: null,
  file_name: "",
  original_file_name: "",
  fileDetails: {
    url: "",
    name: "",
    fileSize: "",
    base64: "",
  },
  previousFileDetails: {
    url: "",
    name: "",
    fileSize: "",
    base64: "",
  },
  showUploadFilePanel: false,
  showDropZoneNew: false,
  validInputs: false,
  errors: {},
  scheduledLiveFiles: [],
  folder_location: "",
  versions: [],
};

const fileNewViewSlice = createSlice({
  name: "fileNewView",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase("LOAD_FILE", (state, action: AnyAction) => {
        Object.assign(state, action.file);
        state.originalStartDate = action.file.scheduled_date;
        state.original_file_name = action.file.file_name;
        state.location_name = action.file.location;
        state.fileDetails.url = action.file.url;
        state.fileDetails.name = action.file.title;
      })
      .addCase("LOAD_FILE_LOCATION_SCHEDULE_LIVE_FILES", (state, action: AnyAction) => {
        state.location_name = action.fileLocationValue;
        state.scheduledLiveFiles = action.scheduledLiveFiles;
      })
      .addCase("SET_SHOW_DROP_ZONE", (state, action: AnyAction) => {
        state.showDropZoneNew = action.show;
        state.showUploadFilePanel = false;
      })
      .addCase("DROP_FILE", (state, action: AnyAction) => {
        const file = action.file[0];
        state.previousFileDetails = {
          ...state.fileDetails,
          file_data: state.file_data,
          file_name: state.file_name,
          title: state.title,
          content_type: state.content_type,
        } as any;
        state.showDropZoneNew = false;
        state.file_data = action.base64;
        state.file_name = file.name;
        state.title = file.name;
        state.fileDetails.url = file.preview;
        (state.fileDetails as any).type = file.type;
        state.fileDetails.fileSize = (file.size / 1024).toFixed(2);
      })
      .addCase("REMOVE_FILE", (state) => {
        state.file_data = null;
        state.file_name = "";
        state.title = "";
        state.url = "";
        state.content_type = "";
      })
      .addCase("REVERT_FILE_DETAILS", (state) => {
        const {
          file_data,
          file_name,
          content_type,
          title,
          url,
          name,
          fileSize,
          base64,
        } = state.previousFileDetails as any;
        state.url = url;
        state.file_data = file_data ?? null;
        state.file_name = file_name ?? "";
        state.content_type = content_type;
        state.title = title ?? "";
        state.fileDetails = {
          url: url,
          name: name,
          ...(fileSize && { fileSize: fileSize }),
          ...(base64 && { base64: base64 }),
        } as any;
      })
      .addCase("SET_FILE_VIEW", (state, action: AnyAction) => {
        state.file_name = action.fileName;
        state.title = action.fileName;
        state.content_type = action.fileType;
        state.url = action.url;
        state.file_data = action.file_data;
      })
      .addCase("RESET_FORM_FIELDS", () => initialState)
      .addCase("SET_DESCRIPTION", (state, action: AnyAction) => {
        state.description = action.description;
      })
      .addCase("SET_NEW_DATE", (state, action: AnyAction) => {
        let timeString,
          combinedString,
          newISODateTime = null,
          dateToSet,
          dbDate;
        if (action.dateString) {
          dateToSet = state.inputISOScheduledDate;
          timeString = dateToSet
            ? dateToSet
            : moment.tz("America/New_York").toISOString();
          timeString = (convertISODateTime(
            timeString
          ) as any)?.toMomentWithConsoleConfigTimeZone?.format("h:mmA");
          combinedString = action.dateString + timeString;
          newISODateTime = convertDefaultTimeZoneStringToISO(combinedString);
          dbDate = (convertISODateTime(newISODateTime) as any).toDBDateTimeString;
        }
        state.scheduled_date = dbDate ?? null;
        state.inputISOScheduledDate = newISODateTime as any;
      })
      .addCase("SET_NEW_TIME", (state, action: AnyAction) => {
        let dateString, combinedString, newISODateTime, dateToSet, dbDate;
        dateToSet = state.inputISOScheduledDate;
        dateString = (convertISODateTime(
          dateToSet
        ) as any)?.toMomentWithConsoleConfigTimeZone?.format("MM/DD/YYYY");
        combinedString = dateString + action.timeString;
        newISODateTime = convertDefaultTimeZoneStringToISO(combinedString);
        dbDate = (convertISODateTime(newISODateTime) as any).toDBDateTimeString;
        state.scheduled_date = dbDate;
        state.inputISOScheduledDate = newISODateTime as any;
      })
      .addCase("ENABLE_POST", (state, action: AnyAction) => {
        state.validInputs = !action.hasErrors;
      })
      .addCase("SET_ERROR", (state, action: AnyAction) => {
        if (action.error.errorName) {
          (state.errors as any)[action.error.errorName] =
            action.error.errorPresent;
        } else {
          Object.assign(state.errors as any, action.error);
        }
      })
      .addCase("DISABLE_SAVE", (state) => {
        state.saveEnabled = false;
      })
      .addCase("ENABLE_SAVE", (state) => {
        state.saveEnabled = true;
      })
      .addCase("SET_FOLDER_LOCATION", (state, action: AnyAction) => {
        state.folder_location = action.folder_location;
      })
      .addCase("SET_LOCATION_NAME", (state, action: AnyAction) => {
        state.location_name = action.location_name;
      })
      .addCase("LOAD_FILE_VERSIONS", (state, action: AnyAction) => {
        state.versions = action.versions;
      });
  },
});

export const selectFileNewView = (state: {
  fileNewView: FileNewViewState;
}): FileNewViewState => state.fileNewView;

export default fileNewViewSlice.reducer;
